import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { KatottgActions, KatottgActionTypes } from '@references/ngrx/actions/katottg.actions';
import {PaginationModel} from '@shared/models/pagination.model';
import {KatottgSearchRequest} from '@services/back-office/model/katottgSearchRequest';
import {ConfigService} from '@config/config.service';
import {KatottgModel} from '@services/back-office/model/katottgModel';

export function sortByCode(a: KatottgModel, b: KatottgModel) {
  return a.code!.localeCompare(b.code!, undefined, { numeric: true });
}

export const adapter: EntityAdapter<KatottgModel> = createEntityAdapter<KatottgModel>({
  sortComparer: sortByCode,
});

export interface State extends EntityState<KatottgModel> {
  loading: boolean,
  pagination: PaginationModel,
  filter: KatottgSearchRequest,
}

export const initialState: State = adapter.getInitialState({
  loading: false,
  pagination: {
    first: true,
    last: false,
    number: 0,
    numberOfElements: 0,
    size: new ConfigService().paginationConfig.pageSize,
    totalElements: 0,
  },
  filter: {},
});

export function reducer(state = initialState, action: KatottgActions): State {
  switch (action.type) {
    /** Load Katottg */
    case KatottgActionTypes.LoadKatottg:
      return {...state, loading: !action.payload?.dontShowLoading};

    case KatottgActionTypes.LoadKatottgSuccess:
      return adapter.setAll(action.payload.content!, {
        ...state,
        loading: false,
        pagination: {
          size: action.payload.size,
          last: action.payload.last,
          totalElements: action.payload.totalElements,
          numberOfElements: action.payload.numberOfElements,
          first: action.payload.first,
          number: action.payload.number,
        },
      });

    case KatottgActionTypes.LoadKatottgFailure:
      return {...state, loading: false};

    /** Set Katottg Location */
    case KatottgActionTypes.SetKatottgLocation:
      return {...state};

    case KatottgActionTypes.SetKatottgLocationSuccess:
      return adapter.updateOne({
        id: action.payload.id!,
        changes: {
          ...action.payload,
          polyPoints: action.payload.polyPoints ?? [],
        },
      }, {...state});

    case KatottgActionTypes.SetKatottgLocationFailure:
      return {...state};

      /** Add  Katottg City Region */
    case KatottgActionTypes.AddKatottgCityRegion:
      return {...state};

    case KatottgActionTypes.AddKatottgCityRegionSuccess:
      return adapter.addOne(action.payload, {...state, loading: false});

    case KatottgActionTypes.AddKatottgCityRegionFailure:
      return {...state};

      /** Edit Katottg City Region */
    case KatottgActionTypes.EditKatottgCityRegion:
      return {...state};

    case KatottgActionTypes.EditKatottgCityRegionSuccess:
      return adapter.upsertOne(action.payload, {...state, loading: false});

    case KatottgActionTypes.EditKatottgCityRegionFailure:
      return {...state};

      /** Delete Katottg City Region */
    case KatottgActionTypes.DeleteKatottgCityRegion:
      return {...state};

    case KatottgActionTypes.DeleteKatottgCityRegionSuccess:
      return adapter.removeOne(action.payload, {
        ...state,
        pagination: {
          ...state.pagination,
          numberOfElements: state.pagination.numberOfElements !== 0 ? state.pagination.numberOfElements! - 1 : 0
        }
      });

    case KatottgActionTypes.DeleteKatottgCityRegionFailure:
      return {...state};

      /** Change Page */
    case KatottgActionTypes.ChangePage:
      return {
        ...state,
        pagination: {
          ...state.pagination,
          number: action.payload.number,
          size: action.payload.size,
        }
      };

      /** Set Initial Pagination */
    case KatottgActionTypes.SetInitialPagination:
      return {...state, pagination: initialState.pagination};

      /** Set Filter */
    case KatottgActionTypes.SetFilter:
      return {...state, filter: action.payload};

      /** Clear Filter */
    case KatottgActionTypes.ClearFilter:
      return {...state, filter: initialState.filter};

    /** Set Initial State */
    case KatottgActionTypes.SetInitialState:
      return {...initialState};

    default:
      return state;
  }
}

export const {
  selectIds: selectKatottgIds,
  selectEntities: selectKatottgEntities,
  selectAll: selectAllKatottg,
  selectTotal: selectKatottgTotal,
} = adapter.getSelectors();
